/**
 * AuthContext — estado global de autenticación AetherLite.
 *
 * Modelo:
 *   - Login exclusivo por Google Sign-In (id_token → /aetherlite/auth/google).
 *   - JWT propio de 30 días en localStorage (`aetherlite.access_token`).
 *   - No hay refresh: si vence, escuchamos `aetherlite-auth-expired` y limpiamos.
 *
 * API expuesta:
 *   { user, loading, loginGoogle, logout, refreshUser, updateProfile }
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { ApiError, AUTH_EXPIRED_EVENT, getToken } from "../api/client";
import {
  getMe,
  loginGoogle as apiLoginGoogle,
  logout as apiLogout,
  updateMe,
  type AetherLiteUser,
} from "../api/auth";

interface AuthContextValue {
  user: AetherLiteUser | null;
  loading: boolean;
  /** Recibe el id_token devuelto por Google Sign-In y completa el login. */
  loginGoogle: (idToken: string) => Promise<AetherLiteUser>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateProfile: (data: Partial<AetherLiteUser>) => Promise<AetherLiteUser>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AetherLiteUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: si hay token en localStorage, intentar GET /me.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    getMe()
      .then((u) => setUser(u))
      .catch((e) => {
        // 401/403 ya limpia el token vía AUTH_EXPIRED_EVENT en client.ts.
        // Para otros errores (red, 500…) dejamos el token y mostramos login —
        // el user puede reintentar refresh manual luego.
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          apiLogout();
        }
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Escuchar evento global "auth expired" (disparado por client.ts ante 401).
  useEffect(() => {
    const handler = () => {
      setUser(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, []);

  const loginGoogle = useCallback(async (idToken: string) => {
    const res = await apiLoginGoogle(idToken);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
    // Best-effort: si el SDK de Google está cargado, también deshabilitamos
    // auto-select para que la próxima vez el user vea el botón limpio.
    try {
      window.google?.accounts.id.disableAutoSelect();
    } catch {
      // ignore
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await getMe();
    setUser(u);
  }, []);

  const updateProfile = useCallback(async (data: Partial<AetherLiteUser>) => {
    const u = await updateMe(data);
    setUser(u);
    return u;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, loginGoogle, logout, refreshUser, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() debe estar dentro de <AuthProvider>");
  return ctx;
}
